import { ChevronRight as ChevronRightIcon } from "@tamagui/lucide-icons";
import { Avatar, type AvatarProps } from "../Avatar/Avatar";
import { Icon, type IconComponent } from "../Icon";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { RowLayout } from "../Row/RowLayout";
import { Body1 } from "../Typography";
import { Card, type CardProps, type CardSize } from "./Card";
export type ResourceCardProps = Omit<CardProps, "children" | "size"> & Pick<AvatarProps, "status" | "loading"> & {
  size?: CardSize;
  /** Forwarded to Avatar. */
  icon: IconComponent;
  label: string;

  // interactive cards default to a chevron-right; pass an explicit icon here to swap it.
  trailing?: IconComponent | null;
};

/**
 * Inline chat-history card representing an upstream resource (connection
 * status, file attachment, etc.). Avatar leads the card; pass `kind="button"`
 * with `onPress` or `kind="link"` with `href` to make it tappable.
 */
export const ResourceCard = ({
  status = "neutral",
  icon,
  label,
  loading,
  size = "small",
  trailing,
  "aria-label": ariaLabel,
  ...rest
}: ResourceCardProps) => {
  // Used here only to default `trailing` to a chevron when the card navigates.
  const {
    isInteractive
  } = useInteractiveElement(rest);
  // Default to chevron-right on interactive cards; respect explicit overrides (including `null` to suppress).
  const resolvedTrailing = trailing === undefined ? isInteractive ? ChevronRightIcon : undefined : trailing ?? undefined;
  return <Card size={size} padding="$sp1" paddingRight="$sp2" {...isInteractive ? {
    "aria-label": ariaLabel ?? label
  } : {}} {...rest} maxWidth="100%" data-sentry-element="Card" data-sentry-component="ResourceCard" data-sentry-source-file="ResourceCard.tsx">
      <RowLayout leading={<Avatar shape="square" size={size === "large" ? "medium" : "small"} icon={icon} status={status} loading={loading} />} primary={<Body1 flexShrink={1} numberOfLines={1}>
            {label}
          </Body1>} primaryTrailing={resolvedTrailing ? <Icon icon={resolvedTrailing} size={size === "large" ? "$iconLarge" : "$iconSmall"} color="$iconSubtle" /> : undefined} data-sentry-element="RowLayout" data-sentry-source-file="ResourceCard.tsx" />
    </Card>;
};